import axiosInstance from '@/setup/axios/axiosInstance';

export const TRANSACTION_STATUS_MODULE = 'transaction';

export type TransactionStatusOption = {
  id: string;
  name: string;
  code?: string;
  is_default?: boolean;
  is_active?: boolean;
  color?: string;
  sort_order?: number;
  group?: {
    id?: string;
    name?: string;
    sort_order?: number;
  };
};

const mapStatusItem = (
  item: Record<string, unknown>,
  groupStatus?: Record<string, unknown> | null,
): TransactionStatusOption => ({
  id: String(item.id ?? ''),
  name: String(item.name ?? ''),
  code: item.code ? String(item.code) : undefined,
  is_default: Boolean(item.is_default),
  is_active: item.is_active !== false,
  color: item.color ? String(item.color) : undefined,
  sort_order: typeof item.sort_order === 'number' ? item.sort_order : 0,
  group: groupStatus
    ? {
        id: groupStatus.id ? String(groupStatus.id) : undefined,
        name: groupStatus.name ? String(groupStatus.name) : undefined,
        sort_order:
          typeof groupStatus.sort_order === 'number' ? groupStatus.sort_order : 999,
      }
    : undefined,
});

export const normalizeTransactionStatusesFromPayload = (
  data: unknown,
  moduleKey: string = TRANSACTION_STATUS_MODULE,
): TransactionStatusOption[] => {
  const root = (data as { data?: unknown })?.data ?? data;
  const record = (root ?? {}) as Record<string, unknown>;

  const modulePayload =
    record[moduleKey]
    ?? (record.module === moduleKey ? record : null)
    ?? record;

  if (!modulePayload || typeof modulePayload !== 'object') {
    return [];
  }

  const payload = modulePayload as Record<string, unknown>;

  if (Array.isArray(payload.groups)) {
    return payload.groups.flatMap((group) => {
      const groupRecord = group as Record<string, unknown>;
      const groupStatus = (groupRecord.group_status as Record<string, unknown> | null) ?? null;
      const items = Array.isArray(groupRecord.items) ? groupRecord.items : [];
      return items
        .map((item) => mapStatusItem(item as Record<string, unknown>, groupStatus))
        .filter((item) => item.id && item.is_active !== false);
    });
  }

  if (Array.isArray(payload.items)) {
    return payload.items
      .map((item) =>
        mapStatusItem(
          item as Record<string, unknown>,
          (item as Record<string, unknown>).group_status as Record<string, unknown> | null,
        ),
      )
      .filter((item) => item.id && item.is_active !== false);
  }

  if (Array.isArray(modulePayload)) {
    return modulePayload
      .map((item) => mapStatusItem(item as Record<string, unknown>))
      .filter((item) => item.id && item.is_active !== false);
  }

  return [];
};

export async function fetchTransactionStatusesByModule(
  moduleKey: string = TRANSACTION_STATUS_MODULE,
): Promise<TransactionStatusOption[]> {
  const response = await axiosInstance.get('/settings/statuses/by-module/', {
    params: { module: moduleKey },
  });
  return normalizeTransactionStatusesFromPayload(response.data, moduleKey);
}
